import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { auth } from 'src/services/firebase-service';
import logging from 'src/services/logging';

import {
  Backdrop,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

const ResetPage = () => {
  const [verifying, setVerifying] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(false);
  const [changing, setChanging] = useState<boolean>(false);
  const [oobCode, setOobCode] = useState<string>('');
  const [error, setError] = useState<string>('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    logging.info('Extracting code.', 'ResetPage');

    let code = searchParams.get('oobCode');

    if (code) {
      logging.info('Code found.', 'ResetPage');
      verifyPasswordResetLink(code);
    } else {
      logging.error('Unable to find code.', 'ResetPage');
      setVerified(false);
      setVerifying(false);
    }
  }, [searchParams]);

  const verifyPasswordResetLink = async (code: string) => {
    try {
      const response = await verifyPasswordResetCode(auth, code);
      logging.info(`Resetting password for: ${response}`, 'ResetPage');
      setOobCode(code);
      setVerified(true);
      setVerifying(false);
    } catch (error) {
      logging.error(error, 'ResetPage');
      setVerified(false);
      setVerifying(false);
    }
  };

  const passwordReset = async (data: any) => {
    try {
      if (data.password !== data.confirm) {
        setError('Please make sure your passwords match.');
        return;
      }

      if (error !== '') setError('');

      setChanging(true);

      await confirmPasswordReset(auth, oobCode, data.password);

      logging.info('Successfully reset password.', 'ResetPage');
      navigate('/login');
    } catch (error: any) {
      logging.error(error.message, 'ResetPage');
      setError(error.message);
      setChanging(false);
    }
  };

  return (
    <Container maxWidth='sm'>
      {verifying ? (
        <Backdrop open={verifying}>
          <CircularProgress color='inherit' />
        </Backdrop>
      ) : (
        <>
          {verified ? (
            <Card>
              <CardHeader title='Reset Password' />
              <Divider />
              <CardContent>
                <Formik
                  initialValues={{
                    password: '',
                    confirm: '',
                  }}
                  validationSchema={Yup.object().shape({
                    password: Yup.string().label('Password').min(8).required(),
                    confirm: Yup.string()
                      .label('Confirm Password')
                      .required()
                      .test(
                        'passwords-match',
                        'Passwords must match',
                        (value, context) => context.parent.password === value
                      ),
                  })}
                  onSubmit={(data) => {
                    passwordReset(data);
                  }}
                >
                  {({ errors, handleChange, isValid, touched }) => (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            name='password'
                            type='password'
                            label='Password'
                            fullWidth={true}
                            error={!!(touched.password && errors.password)}
                            helperText={touched.password && errors.password}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name='confirm'
                            type='password'
                            label='Confirm Password'
                            fullWidth={true}
                            error={!!(touched.confirm && errors.confirm)}
                            helperText={touched.confirm && errors.confirm}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            disabled={changing || !isValid}
                            variant='contained'
                            fullWidth={true}
                            type='submit'
                          >
                            {changing ? (
                              <CircularProgress size={24} color='inherit' />
                            ) : (
                              'Reset Password'
                            )}
                          </Button>
                        </Grid>
                        {error && (
                          <Grid item>
                            <Typography sx={{ color: '#d32f2f' }}>
                              {error}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader title='Invalid Link'></CardHeader>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default ResetPage;
